import { Icon } from "@/components/ui/Icon";
import { useSpace } from "@/hooks/useSpace";
import { BIZ_META, SPACE_META, TENANT_BIZ, bizGate, spaceHomePath, type BizKey, type TenantKey } from "@/domain/space";
import { isHomeKey, menuPath, navFor, type NavItem } from "@/domain/menus";

/**
 * AllMenus — "전체 보기" 메뉴 한 장 (B′). 2뎁스까지만: 법인별 칸 > 그룹 > 메뉴 링크. 3뎁스(탭) 없음.
 * 링크를 누르면 그 법인 공간으로 전환한 뒤 이동한다. 권한(gate) 없는 메뉴는 보이지 않는다.
 */
interface Group { label: string; biz: BizKey; items: NavItem[] }

/** 법인의 사업(권한 있는 것)마다 묶음. 두 번째 사업부터는 묶음 이름 앞에 사업 이름, 같은 메뉴(법인 공통)는 첫 사업에만 */
function groupsOf(t: TenantKey, hasPermIn: (k: string, t: TenantKey) => boolean): Group[] {
  const out: Group[] = [];
  const seen = new Set<string>();
  const bizs = TENANT_BIZ[t].filter((b) => { const g = bizGate(b); return !g || hasPermIn(g, t); });
  bizs.forEach((b, i) => {
    const prefix = i > 0 ? BIZ_META[b].label : "";
    let cur: Group | null = null;
    for (const n of navFor(t, b)) {
      if (n.type === "group") { cur = { label: [prefix, n.label].filter(Boolean).join(" "), biz: b, items: [] }; out.push(cur); continue; }
      if (isHomeKey(n.key) || seen.has(n.key)) continue;
      if (n.gate && !n.gate.some((k) => hasPermIn(k, t))) continue;
      seen.add(n.key);
      if (!cur) { cur = { label: prefix, biz: b, items: [] }; out.push(cur); }
      cur.items.push(n);
    }
  });
  return out.filter((g) => g.items.length > 0);
}

export function AllMenus() {
  const { tenants, switchSpace, hasPermIn } = useSpace();

  if (tenants.length === 0) {
    return (
      <div className="card my-allmenu-empty">
        <Icon name="info" size={20} />
        <span>업무 공간이 열린 법인이 없습니다. 기사 계정은 내 업무만 사용합니다.</span>
      </div>
    );
  }

  return (
    <div className={`my-allmenu${tenants.length === 1 ? " is-single" : ""}`}>
      {tenants.map((t) => {
        const m = SPACE_META[t];
        const groups = groupsOf(t, hasPermIn);
        return (
          <section key={t} className={`card my-allmenu-col is-space-${t}`}>
            <div className="my-allmenu-hd">
              <span className="sp-dot" aria-hidden="true" />
              <span className="my-allmenu-name">{m.label}</span>
              {m.sub && <span className="my-allmenu-sub">{m.sub}</span>}
              <button type="button" className="btn-text my-allmenu-home" onClick={() => switchSpace(t, { to: spaceHomePath(t) })}>
                홈 <Icon name="chevR" size={12} />
              </button>
            </div>
            {groups.length === 0 ? (
              <div className="my-allmenu-none">열 수 있는 메뉴가 없습니다</div>
            ) : (
              <div className="my-allmenu-grid">
                {groups.map((g, gi) => (
                  <div key={g.label || gi} className="my-allmenu-group">
                    {g.label && <div className="my-allmenu-glabel">{g.label}</div>}
                    <div className="my-allmenu-links">
                      {g.items.map((it) => (
                        <button
                          key={it.key}
                          type="button"
                          className="my-allmenu-link"
                          onClick={() => switchSpace(t, { to: menuPath(it.key, t, g.biz) })}
                        >
                          {it.label}
                          {it.comingSoon && <span className="my-allmenu-soon">준비중</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
